import { useEffect, useState } from "react";
import { Building2, RefreshCw, Search, UserRound, X, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { useI18n } from "@/hooks/useI18n";
import { usePlatformFeeOwners } from "@/hooks/usePlatformFee";
import type { PlatformFeeOwnerKind } from "@/integrations/supabase/rpc-types";
import { AgenciesEmpty, AgenciesTable } from "@/lib/dashboard/agencies/AgenciesTable";
import { PlatformFeeOwnerSheet } from "@/lib/dashboard/agencies/PlatformFeeOwnerSheet";
import type { TranslationKey } from "@/types/i18n.types";

const PAGE_SIZE = 12;
const SEARCH_DEBOUNCE_MS = 350;

const SECTIONS: { value: PlatformFeeOwnerKind; labelKey: TranslationKey; icon: typeof Building2 }[] = [
  { value: "agency", labelKey: "dashboard.overview.agencies.sectionAgencies", icon: Building2 },
  { value: "independent", labelKey: "dashboard.overview.agencies.sectionIndependents", icon: UserRound },
];

/**
 * Tab Agenzie di Dati, solo admin: agenzie e advisor indipendenti, in due sezioni, con
 * l'aliquota della fee Place2Be di ciascuno. Da qui si contratta: la scheda della riga
 * modifica l'aliquota e ne mostra lo storico.
 */
export function AgenciesTab() {
  const { t } = useI18n();
  const [kind, setKind] = useState<PlatformFeeOwnerKind>("agency");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [onlyCustom, setOnlyCustom] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const timer = setTimeout(
      () => setSearch(searchInput),
      searchInput.trim() ? SEARCH_DEBOUNCE_MS : 0
    );
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Stessa regola della tab Viaggi: la pagina vale per la combinazione di filtri in cui e'
  // stata scelta, cambiando filtro si riparte dalla prima.
  const filtersKey = JSON.stringify([kind, search, onlyCustom]);
  const [pageState, setPageState] = useState({ key: filtersKey, page: 1 });
  const page = pageState.key === filtersKey ? pageState.page : 1;
  const setPage = (next: number) => setPageState({ key: filtersKey, page: next });

  const { data, loading, refreshing, error, version } = usePlatformFeeOwners(
    { kind, search, onlyCustom, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE },
    refreshKey
  );
  // Contatori delle due sezioni: ignorano ricerca e filtro, come i contatori dei Viaggi.
  const agencySummary = usePlatformFeeOwners({ kind: "agency", limit: 1, offset: 0 }, refreshKey);
  const independentSummary = usePlatformFeeOwners({ kind: "independent", limit: 1, offset: 0 }, refreshKey);
  const summaries = { agency: agencySummary, independent: independentSummary };

  const rows = data?.rows ?? [];
  const totalCount = data?.total ?? 0;
  const defaultRate = data?.default_rate ?? agencySummary.data?.default_rate ?? null;
  const selectedRow = rows.find((row) => row.id === selectedId) ?? null;
  const hasFilters = Boolean(search.trim()) || onlyCustom;

  const handleRefresh = () => setRefreshKey((key) => key + 1);

  if (error) {
    return (
      <Card>
        <CardContent>
          <div className="flex flex-col items-center gap-3 py-12 text-center">
            <div className="flex items-center justify-center h-12 w-12 rounded-full bg-red-50 text-red-500">
              <XCircle className="h-6 w-6" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-gray-900">{t("dashboard.overview.errorTitle")}</p>
              <p className="text-sm text-gray-500">{t("dashboard.overview.errorDescription")}</p>
            </div>
            <Button variant="outline" size="sm" onClick={handleRefresh} className="mt-1">
              <RefreshCw className="h-4 w-4 mr-2" />
              {t("dashboard.retry")}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-1 flex-col min-h-0 gap-3 pt-0 md:pt-2">
      <div className="shrink-0 grid grid-cols-2 gap-3 max-w-xl">
        {SECTIONS.map(({ value, labelKey, icon: Icon }) => {
          const selected = kind === value;
          const summary = summaries[value];
          return (
            <button
              key={value}
              type="button"
              aria-pressed={selected}
              onClick={() => {
                setKind(value);
                setSelectedId(null);
              }}
              className={`text-left rounded-lg border px-4 py-3 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                selected
                  ? "border-primary bg-primary/5 ring-1 ring-primary"
                  : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
              }`}
            >
              <div className={`flex items-center gap-1.5 text-xs ${selected ? "text-primary font-medium" : "text-gray-500"}`}>
                <Icon className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{t(labelKey)}</span>
              </div>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-2xl font-semibold text-gray-900 tabular-nums">
                  {summary.loading ? <Skeleton className="h-8 w-12" /> : summary.data?.total ?? "–"}
                </span>
                {!summary.loading && (summary.data?.custom_count ?? 0) > 0 && (
                  <span className="text-xs text-gray-500">
                    {t("dashboard.overview.agencies.customCount", { count: summary.data!.custom_count })}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <div className="shrink-0 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t("dashboard.overview.agencies.searchPlaceholder")}
            className="pl-9 pr-9"
            aria-label={t("dashboard.overview.agencies.searchPlaceholder")}
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
              aria-label={t("dashboard.overview.clients.clearSearch")}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 px-1">
          <Switch id="platform-fee-only-custom" checked={onlyCustom} onCheckedChange={setOnlyCustom} />
          <Label htmlFor="platform-fee-only-custom" className="text-sm text-gray-700 cursor-pointer">
            {t("dashboard.overview.agencies.onlyCustom")}
          </Label>
        </div>

        <Button
          variant="outline"
          size="icon"
          onClick={handleRefresh}
          disabled={loading || refreshing}
          aria-label={t("dashboard.retry")}
        >
          <RefreshCw className={`h-4 w-4 ${loading || refreshing ? "animate-spin" : ""}`} />
        </Button>
      </div>

      <div className="shrink-0 text-sm text-gray-500">
        {loading ? (
          <Skeleton className="h-4 w-24 my-0.5" />
        ) : (
          t(kind === "agency"
            ? "dashboard.overview.agencies.countAgencies"
            : "dashboard.overview.agencies.countIndependents",
            { count: totalCount })
        )}
      </div>

      <div className="flex-1 min-h-0">
        <AgenciesTable
          kind={kind}
          rows={rows}
          selectedId={selectedId}
          onSelect={(row) => setSelectedId(row.id)}
          pageSize={PAGE_SIZE}
          serverPagination={{ page, totalItems: totalCount, onPageChange: setPage, disabled: refreshing }}
          isLoading={loading || refreshing}
          animationKey={version}
          emptyState={<AgenciesEmpty filtered={hasFilters} />}
        />
      </div>

      {defaultRate !== null && (
        <PlatformFeeOwnerSheet
          kind={kind}
          owner={selectedRow}
          defaultRate={defaultRate}
          onOpenChange={(open) => {
            if (!open) setSelectedId(null);
          }}
          onSaved={handleRefresh}
        />
      )}
    </div>
  );
}

export default AgenciesTab;
